import { cloneElement, useEffect } from "react";

import { Box, useDisclosure } from "@chakra-ui/react";
import { useIntl } from "react-intl";

import { InventoryChangeReason } from "__graphql__/types";
import { useCloudinaryPhotoUpload } from "app/hooks/useCloudinaryPhotoUpload";
import { config } from "config";
import { reasonMetadata } from "flows/Inventory/flows/StockCorrections/utils/constants"; // TODO: move to a more general location
import { transformChunks } from "shared/components/TextWrapper";
import { Accordion } from "ui/Accordion/Accordion";
import { BottomSheet } from "ui/BottomSheet/BottomSheet";
import { Button } from "ui/Button/Button";
import { InformationIcon } from "ui/Icons/Icons";
import { PhotoUpload } from "ui/PhotoUpload/PhotoUpload";
import { TitleM, DetailL, BodyS } from "ui/Typography/Typography";

const { CLOUDINARY_FOLDER } = config.environment;

function OutboundPhotoUpload({
  onPhotosChanged,
}: {
  onPhotosChanged?: (uploadedPhotos: string[]) => void;
}) {
  const intl = useIntl();

  const {
    isOpen: isBottomSheetOpen,
    onOpen: openBottomSheet,
    onClose: closeBottomSheet,
  } = useDisclosure();

  const { uploadedPhotos, uploadPhoto, removePhoto } = useCloudinaryPhotoUpload(CLOUDINARY_FOLDER);

  useEffect(() => {
    if (onPhotosChanged) {
      onPhotosChanged(uploadedPhotos.map((photo) => photo.src));
    }
  }, [uploadedPhotos, onPhotosChanged]);

  return (
    <>
      <Box bg="white" borderRadius="8px" p="s200">
        <TitleM color="grey.800" mb="s25">
          {intl.formatMessage({ id: "pages.inbound.inbound-outbound.outbound-photo-upload.title" })}
        </TitleM>
        <Button
          variant="ghost"
          size="xs"
          p="0"
          m="0"
          mb="s200"
          onClick={openBottomSheet}
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          gap="s50"
          _hover={{ bg: "transparent" }}
          _active={{ bg: "transparent" }}
        >
          <DetailL color="blue.500">
            {intl.formatMessage({
              id: "pages.inbound.inbound-outbound.outbound-photo-upload.explanations",
            })}
          </DetailL>
          <InformationIcon color="blue.500" boxSize="15px" />
        </Button>
        <PhotoUpload
          label={intl.formatMessage(
            {
              id: "pages.inbound.inbound-outbound.outbound-photo-upload.explanations.call-to-action",
            },
            { br: <br /> },
          )}
          onPhotoUpload={uploadPhoto}
          onPhotoRemove={removePhoto}
          uploadedPhotos={uploadedPhotos}
        />
      </Box>
      <BottomSheet isOpen={isBottomSheetOpen} onClose={closeBottomSheet}>
        <Accordion allowMultiple defaultIndex={[0, 1]}>
          <Accordion.Item>
            <Accordion.ItemTrigger>
              {reasonMetadata[InventoryChangeReason.outbound_product_damaged].icon &&
                cloneElement(reasonMetadata[InventoryChangeReason.outbound_product_damaged].icon, {
                  boxSize: "24px",
                  marginRight: "4px",
                })}
              <BodyS mx="s100">
                {intl.formatMessage({
                  id: "pages.inbound.inbound-outbound.outbound-reason.damaged",
                })}
              </BodyS>
              <Accordion.ItemIndicator />
            </Accordion.ItemTrigger>
            <Accordion.ItemContent>
              <DetailL>
                {intl.formatMessage(
                  {
                    id: "pages.inbound.inbound-outbound.outbound-photo-upload.explanations.damaged.body",
                  },
                  { ...transformChunks({ b: { as: "strong" } }), br: <br /> },
                )}
              </DetailL>
            </Accordion.ItemContent>
          </Accordion.Item>
          <Accordion.Item>
            <Accordion.ItemTrigger>
              {reasonMetadata[InventoryChangeReason.outbound_product_expired].icon &&
                cloneElement(reasonMetadata[InventoryChangeReason.outbound_product_expired].icon, {
                  boxSize: "24px",
                  marginRight: "4px",
                })}
              <BodyS mx="s100">
                {intl.formatMessage({
                  id: "pages.inbound.inbound-outbound.outbound-reason.expired",
                })}
              </BodyS>
            </Accordion.ItemTrigger>
            <Accordion.ItemContent>
              <DetailL>
                {intl.formatMessage(
                  {
                    id: "pages.inbound.inbound-outbound.outbound-photo-upload.explanations.expired.body",
                  },
                  { ...transformChunks({ b: { as: "strong" } }), br: <br /> },
                )}
              </DetailL>
            </Accordion.ItemContent>
          </Accordion.Item>
        </Accordion>
      </BottomSheet>
    </>
  );
}

export default OutboundPhotoUpload;
